import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useManagerMeApi } from '@/services/managers'
import { resolveManagerAccountContext } from '@/composables/managers/useResolveManagerAccount'
import { resolveManagerOnboardingRedirect } from '@/router/resolveManagerOnboardingRedirect'
import { can } from '@/utils/managerPermissions'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: { name: 'dashboard' },
    },
    {
      path: '/callback',
      name: 'callback',
      component: () => import('@/views/CallbackPage.vue'),
      meta: {
        requiresAuth: false,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
      },
    },
    {
      path: '/swap-virtual-card',
      name: 'swap-virtual-card',
      component: () => import('@/views/SwapVirtualCardPage.vue'),
      meta: {
        requiresAuth: false,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
        skipExpiredCorpSessionRefresh: true,
      },
    },
    {
      path: '/app/dados-cartao',
      name: 'swap-virtual-card-mobile',
      component: () => import('@/views/SwapVirtualCardMobilePage.vue'),
      meta: {
        requiresAuth: false,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
        skipExpiredCorpSessionRefresh: true,
      },
    },
    {
      path: '/cadastro-empresa',
      redirect: { name: 'register-business' },
    },
    {
      path: '/register-business',
      name: 'register-business',
      component: () => import('@/views/register-business/RegisterBusinessPage.vue'),
      meta: {
        requiresAuth: false,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
      },
    },
    {
      path: '/auth/onboarding',
      name: 'onboarding',
      component: () => import('@/views/complete-register/CompleteRegisterFlowPage.vue'),
      meta: {
        requiresAuth: false,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
      },
    },
    {
      path: '/auth/create-password',
      name: 'create-password',
      redirect: (to) => ({ name: 'onboarding', query: { ...to.query, token: to.query.token, step: '0' } }),
    },
    {
      path: '/auth/accept-terms',
      name: 'accept-terms',
      redirect: () => ({ name: 'onboarding', query: { step: '1' } }),
    },
    {
      path: '/auth/validate-data',
      name: 'validate-data',
      redirect: () => ({ name: 'onboarding', query: { step: '2' } }),
    },
    {
      path: '/auth/edit-business',
      name: 'edit-business',
      component: () => import('@/views/auth/EditBusinessPage.vue'),
      meta: {
        requiresAuth: true,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
      },
    },
    {
      path: '/auth/in-analysis',
      name: 'in-analysis',
      component: () => import('@/views/auth/InAnalysisPage.vue'),
      meta: {
        requiresAuth: true,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
      },
    },
    {
      path: '/auth/rejected',
      name: 'rejected',
      component: () => import('@/views/auth/RejectedPage.vue'),
      meta: {
        requiresAuth: true,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
      },
    },
    {
      path: '/auth/acesso-negado',
      name: 'access-denied',
      component: () => import('@/views/auth/AccessDeniedPage.vue'),
      meta: {
        requiresAuth: true,
        requiresAccount: true,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
        scrollMainContent: false,
      },
    },
    {
      path: '/auth/complete-shareholder',
      redirect: () => ({ name: 'onboarding', query: { step: '3' } }),
    },
    {
      path: '/auth/complete-shareholder/form',
      redirect: () => ({ name: 'onboarding', query: { step: '3' } }),
    },
    {
      path: '/select-account',
      name: 'select-account',
      component: () => import('@/views/auth/SelectAccountPage.vue'),
      meta: {
        requiresAuth: true,
        requiresAccount: false,
        showSidebar: false,
        showToolbar: false,
        showSmallScreenBanner: false,
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/dashboard/DashboardPage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/colaboradores',
      redirect: { name: 'managed-users' },
    },
    {
      path: '/grupos',
      name: 'groups',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    {
      path: '/cartoes',
      name: 'cards',
      component: () => import('@/views/CardsPage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/cartoes/novo',
      name: 'create-card',
      component: () => import('@/views/CreateCardPage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/cartoes/modelos/novo',
      name: 'create-card-template',
      component: () => import('@/views/CardTemplateCreatePage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/cartoes/modelos/:id/editar',
      name: 'edit-card-template',
      component: () => import('@/views/CardTemplateCreatePage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/cartoes/modelos',
      name: 'card-models',
      component: () => import('@/views/CardUsageModelsPage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/cartoes/pedidos',
      name: 'card-orders',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/cartoes/:id',
      name: 'card-detail',
      component: () => import('@/views/CardDetailPage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false, scrollMainContent: false },
    },
    {
      path: '/cartoes/:id/editar',
      name: 'card-edit',
      component: () => import('@/views/EditCardPage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/transacoes/:transactionId/solicitar-ajuste-comprovante',
      redirect: (to) => ({
        path: '/transacoes',
        query: {
          ...(to.query as Record<string, string>),
          ajusteComprovante: Array.isArray(to.params.transactionId)
            ? String(to.params.transactionId[0])
            : String(to.params.transactionId),
        },
      }),
    },
    {
      path: '/transacoes/:transactionId/reprovar-comprovante',
      redirect: (to) => ({
        path: '/transacoes',
        query: {
          ...(to.query as Record<string, string>),
          reprovarComprovante: Array.isArray(to.params.transactionId)
            ? String(to.params.transactionId[0])
            : String(to.params.transactionId),
        },
      }),
    },
    {
      path: '/transacoes',
      name: 'transactions',
      component: () => import('@/views/transactions/TransactionsPage.vue'),
      meta: { requiresAuth: true, permission: { resource: 'transactions', action: 'index' } },
    },
    {
      path: '/comprovantes',
      name: 'receipts',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    {
      path: '/relatorios',
      name: 'reports',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    {
      path: '/configuracoes',
      name: 'settings',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    // Gestão
    {
      path: '/equipe',
      name: 'teams',
      component: () => import('@/views/teams/TeamsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/equipe/:id',
      name: 'team-detail',
      component: () => import('@/views/teams/TeamDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/usuarios',
      name: 'managed-users',
      component: () => import('@/views/users/UsersPage.vue'),
      meta: { requiresAuth: true },
    },
    // Financeiro
    {
      path: '/aprovacoes',
      name: 'approvals',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    {
      path: '/conta-digital',
      component: () => import('@/views/digital-account/DigitalAccountLayout.vue'),
      meta: { requiresAuth: true, permission: { resource: 'balance', action: 'show' } },
      children: [
        {
          path: '',
          name: 'digital-account',
          component: () => import('@/views/digital-account/DigitalAccountPage.vue'),
        },
        {
          path: 'chaves-pix',
          name: 'digital-account-keys',
          component: () => import('@/views/digital-account/PixListsPage.vue'),
        },
      ],
    },
    // Frota
    {
      path: '/veiculos',
      name: 'vehicles',
      component: () => import('@/views/vehicles/VehiclesDashboardPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/veiculos/novo',
      name: 'create-vehicle',
      component: () => import('@/views/vehicles/create/CreateVehiclePage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/veiculos/:vehicleId/editar',
      name: 'edit-vehicle',
      component: () => import('@/views/vehicles/edit/EditVehiclePage.vue'),
      meta: { requiresAuth: true, showSmallScreenBanner: false },
    },
    {
      path: '/veiculos/:vehicleId',
      name: 'vehicle-details',
      component: () => import('@/views/vehicles/details/VehicleDetailsPage.vue'),
      meta: { requiresAuth: true, scrollMainContent: false },
    },
    {
      path: '/postos',
      name: 'gas-stations',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    {
      path: '/bomba-interna',
      name: 'internal-pump',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    // Relatórios
    {
      path: '/extratos',
      name: 'statements',
      component: () => import('@/views/dashboard/DashboardPage.vue'), // TODO: criar view
      meta: { requiresAuth: true },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'dashboard' },
    },
  ],
})

router.beforeEach(async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/').replace(/\/$/, '')

  // Cookie + refresh se access expirou (await evita redirect antes do refresh terminar).
  // Rotas SWAP na query não devem disparar refresh expirado → redirect ao Auth (webview mobile sem sessão Corp).
  await authStore.hydrateSession({
    skipExpiredCorpSessionRefresh: to.meta.skipExpiredCorpSessionRefresh === true,
  })

  // Rotas que não precisam de autenticação
  if (to.meta.requiresAuth === false) {
    if (to.name === 'callback' && !authStore.isAuthenticated) {
      const redirectTarget =
        typeof to.query.redirect === 'string' ? to.query.redirect : '/dashboard'
      authStore.redirectToLogin(baseUrl + redirectTarget)
      next(false)
      return
    }
    next()
    return
  }

  // Sem sessão válida: redireciona para login e depois volta para esta rota
  if (!authStore.isAuthenticated) {
    const returnUrl = baseUrl + to.fullPath
    authStore.redirectToLogin(returnUrl)
    return
  }

  // Verifica se precisa de conta selecionada
  const requiresAccount = to.meta.requiresAccount !== false

  if (requiresAccount && !authStore.hasAccountSelected) {
    const outcome = await resolveManagerAccountContext()
    if (outcome.status === 'ready' && outcome.hasAccount) {
      // Conta resolvida — continua abaixo para carregar /manager/me antes de navegar
    } else if (outcome.status === 'ready' && !outcome.hasAccount) {
      if (outcome.redirectTo === 'register-business') {
        next({ name: 'register-business' })
        return
      }
      next({
        name: 'select-account',
        query: { redirect: to.fullPath },
      })
      return
    } else {
      next({
        name: 'select-account',
        query: { redirect: to.fullPath },
      })
      return
    }
  }

  // Se está na página de seleção mas já tem conta, redireciona
  if (to.name === 'select-account' && authStore.hasAccountSelected) {
    const redirect = to.query.redirect as string
    next(redirect || '/dashboard')
    return
  }

  // Rotas autenticadas com conta: garante /manager/me carregado (store como fonte única)
  // 403 authorization_error no /me → tela dedicada (não redireciona mais para termos)
  if (to.meta.requiresAuth && authStore.hasAccountSelected) {
    await userStore.ensureMeLoaded()
    const loadResult = userStore.meLoadResult
    if (
      loadResult &&
      !loadResult.ok &&
      (loadResult.code === 'authorization_error' || loadResult.code === 'access_denied')
    ) {
      if (to.name !== 'access-denied') {
        next({ name: 'access-denied' })
        return
      }
      next()
      return
    }
    if (loadResult && !loadResult.ok && loadResult.code === 'terms_not_accepted') {
      const step = typeof to.query.step === 'string' ? to.query.step : ''
      if (to.name !== 'onboarding' || step !== '1') {
        next({ name: 'onboarding', query: { step: '1' } })
        return
      }
    }
    const { isRejected } = useManagerMeApi()
    const meData = userStore.meData
    if (meData && isRejected(meData) && to.name !== 'rejected') {
      next({ name: 'rejected' })
      return
    }
    if (loadResult?.ok && meData) {
      const onboardingRedirect = resolveManagerOnboardingRedirect(to, meData)
      if (onboardingRedirect) {
        next(onboardingRedirect)
        return
      }
    }

    const routePermission = to.meta.permission as { resource: string; action: string } | undefined
    if (routePermission && loadResult?.ok) {
      const permissions = userStore.meData?.permissions as string[] | undefined
      if (!can(permissions, routePermission.resource, routePermission.action)) {
        next({ name: 'dashboard' })
        return
      }
    }
  }

  next()
})

export default router
